import { useState } from "react";
import { api, apiError } from "../../lib/api";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { PriorityBadge, LabelBadge, Avatar } from "../badges";
import { toast } from "sonner";
import { Plus, CircleDot, CheckCircle2 } from "lucide-react";
import IssueDetail from "./IssueDetail";

export default function IssuesTab({ project, issues, users, reload }) {
  const [filter, setFilter] = useState("open");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", priority: "P2", labels: "", assignee_id: null });
  const userById = (id) => users.find((u) => u.id === id);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/projects/${project.id}/issues`, {
        ...form,
        labels: form.labels.split(",").map((l) => l.trim().toLowerCase()).filter(Boolean),
      });
      toast.success("Issue opened");
      setOpen(false); setForm({ title: "", description: "", priority: "P2", labels: "", assignee_id: null });
      reload();
    } catch (err) { toast.error(apiError(err)); }
  };

  const toggle = async (issue) => {
    try {
      await api.patch(`/issues/${issue.id}`, { status: issue.status === "open" ? "closed" : "open" });
      toast.success(issue.status === "open" ? "Issue closed" : "Issue reopened");
      reload();
    } catch (err) { toast.error(apiError(err)); }
  };

  const shown = issues.filter((i) => i.status === filter);
  const openCount = issues.filter((i) => i.status === "open").length;

  return (
    <div className="space-y-4" data-testid="issues-tab-content">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-2">
          <Button size="sm" variant={filter === "open" ? "default" : "outline"} data-testid="issue-filter-open" onClick={() => setFilter("open")}>
            <CircleDot className="h-3.5 w-3.5 mr-1" /> Open ({openCount})
          </Button>
          <Button size="sm" variant={filter === "closed" ? "default" : "outline"} data-testid="issue-filter-closed" onClick={() => setFilter("closed")}>
            <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Closed ({issues.length - openCount})
          </Button>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-issue-btn"><Plus className="h-4 w-4 mr-1" /> New Issue</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">New Issue</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div className="space-y-1.5"><Label>Title</Label>
                <Input data-testid="issue-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required /></div>
              <div className="space-y-1.5"><Label>Description</Label>
                <Textarea data-testid="issue-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger data-testid="issue-priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{["P0", "P1", "P2", "P3"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-1.5"><Label>Assignee</Label>
                  <Select value={form.assignee_id || "none"} onValueChange={(v) => setForm({ ...form, assignee_id: v === "none" ? null : v })}>
                    <SelectTrigger data-testid="issue-assignee-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
              </div>
              <div className="space-y-1.5"><Label>Labels (comma-separated)</Label>
                <Input data-testid="issue-labels-input" placeholder="bug, firmware" value={form.labels} onChange={(e) => setForm({ ...form, labels: e.target.value })} /></div>
              <Button type="submit" data-testid="issue-create-submit-btn" className="w-full">Open Issue</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="divide-y divide-border">
        {shown.map((i) => (
          <div key={i.id} className="p-4 flex items-start gap-3 hover:bg-accent/40 transition-colors cursor-pointer" data-testid={`issue-row-${i.number}`}
            onClick={() => setActive(i)}>
            {i.status === "open"
              ? <CircleDot className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
              : <CheckCircle2 className="h-4 w-4 text-purple-500 mt-0.5 shrink-0" />}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-sm">{i.title}</span>
                <PriorityBadge priority={i.priority} />
                {(i.labels || []).map((l) => <LabelBadge key={l} label={l} />)}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                #{i.number} opened by {i.created_by_name} · {new Date(i.created_at).toLocaleDateString()}
              </p>
            </div>
            {i.assignee_id && <Avatar name={userById(i.assignee_id)?.name} size="sm" />}
            <Button size="sm" variant="outline" data-testid={`issue-toggle-btn-${i.number}`}
              onClick={(e) => { e.stopPropagation(); toggle(i); }}>
              {i.status === "open" ? "Close" : "Reopen"}
            </Button>
          </div>
        ))}
        {!shown.length && <p className="p-6 text-sm text-muted-foreground">No {filter} issues.</p>}
      </Card>

      {active && <IssueDetail issue={active} users={users} onClose={() => setActive(null)} onChanged={reload} />}
    </div>
  );
}
